import * as React from 'react'
import styled from 'styled-components'
import { screenSizes } from '@src/styles/theme'

interface GatewayOfflineProps {
  onRetry: () => void
  errorMessage?: string | null
  hasToken?: boolean
}

const Card = styled.div`
  background-color: ${(p) => p.theme.modalBack};
  border: 1px solid ${(p) => p.theme.warning};
  border-radius: ${(p) => p.theme.radius};
  padding: 24px;
  margin-bottom: 20px;

  @media (max-width: ${screenSizes.XS}px) {
    padding: 16px;
  }
`

const Heading = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  color: ${(p) => p.theme.warning};
  font-family: 'NunitoSansSemiBold';
`

const Summary = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: ${(p) => p.theme.white};
`

const ErrorText = styled.pre`
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: ${(p) => p.theme.radius};
  background-color: ${(p) => p.theme.input.default};
  color: ${(p) => p.theme.warning};
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
`

const Checklist = styled.ol`
  margin: 0 0 20px;
  padding-left: 20px;
  list-style: decimal;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const CheckItem = styled.li`
  font-size: 14px;
  line-height: 1.5;
  color: ${(p) => p.theme.white};

  code {
    background-color: ${(p) => p.theme.input.default};
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 13px;
    color: ${(p) => p.theme.accent};
    word-break: break-all;
  }
`

const RetryButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  font-size: 14px;
  font-family: 'NunitoSansSemiBold';
  cursor: pointer;
  color: ${(p) => p.theme.white};
  min-height: 44px;
  background-color: ${(p) => p.theme.button.primary.default};

  &:hover {
    background-color: ${(p) => p.theme.button.primary.hover};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
    outline: none;
  }

  @media (max-width: ${screenSizes.XS}px) {
    width: 100%;
  }
`

type GatewayRecoveryState = 'missing-token' | 'unauthorized' | 'expired-token' | 'offline' | 'recovery'

export function classifyGatewayRecoveryState(
  errorMessage: string | null | undefined,
  hasToken: boolean,
): GatewayRecoveryState {
  const normalized = (errorMessage ?? '').toLowerCase()
  if (!hasToken) return 'missing-token'
  if (/\bexpired\b|token expired|jwt expired|session expired/.test(normalized)) {
    return 'expired-token'
  }
  if (/1008|unauthori[sz]ed|invalid token|bad token|permission denied|forbidden/.test(normalized)) {
    return 'unauthorized'
  }
  if (/refused|unavailable|offline|closed|websocket|network|timeout|connect failed/.test(normalized)) {
    return 'offline'
  }
  return 'recovery'
}

const recoveryCopy: Record<GatewayRecoveryState, { heading: string; summary: string; items: string[] }> = {
  'missing-token': {
    heading: 'Gateway Credentials Required',
    summary: 'The gateway needs a token before Equa Web can open the Equabotz control flow.',
    items: [
      'Paste the gateway token into the Token field above.',
      'Confirm the Gateway URL points at the running local gateway.',
      'Select Connect to use the same recovery flow for health, settings, and gateway access.',
    ],
  },
  unauthorized: {
    heading: 'Gateway Authorization Failed',
    summary: 'The gateway rejected the saved credentials. Update the token or password, then reconnect.',
    items: [
      'Replace the token in the Token field above.',
      'If the gateway requires a password, enter it in the Password field above.',
      'Select Connect to retry from this page instead of opening an offline settings screen.',
    ],
  },
  'expired-token': {
    heading: 'Gateway Token Expired',
    summary: 'The saved gateway token is no longer accepted. Generate or paste a fresh token, then reconnect.',
    items: [
      'Clear the expired token from the Token field.',
      'Paste the new gateway token.',
      'Select Connect to refresh the gateway session.',
    ],
  },
  offline: {
    heading: 'Gateway Unavailable',
    summary: 'Equa Web could not reach the gateway. Keep recovery on this page and reconnect after the gateway is running.',
    items: [
      'Verify the Gateway is running with equabot gateway status.',
      'Confirm the Gateway URL matches your setup; the local default is ws://localhost:18789.',
      'Check that local firewall or browser settings allow the WebSocket connection.',
    ],
  },
  recovery: {
    heading: 'Gateway Recovery Needed',
    summary: 'Gateway health, settings, and connect controls use this same recovery path when the gateway is not ready.',
    items: [
      'Review the Gateway URL, Token, and Password fields above.',
      'Select Connect to retry the gateway handshake.',
      'If the issue persists, verify the local gateway process and saved credentials.',
    ],
  },
}

export function GatewayOffline({ onRetry, errorMessage, hasToken = false }: GatewayOfflineProps) {
  const recoveryState = classifyGatewayRecoveryState(errorMessage, hasToken)
  const copy = recoveryCopy[recoveryState]

  return (
    <Card>
      <Heading>{copy.heading}</Heading>
      <Summary>{copy.summary}</Summary>
      {errorMessage && <ErrorText aria-label="Gateway error detail">{errorMessage}</ErrorText>}
      <Checklist>
        {copy.items.map((item) => (
          <CheckItem key={item}>{item}</CheckItem>
        ))}
      </Checklist>
      <RetryButton onClick={onRetry}>Connect</RetryButton>
    </Card>
  )
}
